import React, { useCallback, useEffect, useRef, useState } from 'react';
import Signature, { type SignatureRef } from '@uiw/react-signature';
import { FILL_TASK_FORM_LABELS as L } from '../../constants/labels';

// ─── SVG → PNG data URL ───────────────────────────────────────────────────────
// Serialises the SVG element to a white-background PNG so the data URL can be
// embedded in <img> and sent as a base64 payload without an S3 upload.
//
// Canvas is scaled by devicePixelRatio so the exported PNG is full physical
// resolution on high-DPI displays (e.g. iPad Retina at 2×). Without this the
// PNG would be rendered at logical pixels only, producing a blurry signature
// when printed on a certificate.

function svgToDataUrl(svg: SVGSVGElement): Promise<string> {
  return new Promise((resolve) => {
    const w = svg.clientWidth || 400;
    const h = svg.clientHeight || 204;
    const dpr = window.devicePixelRatio || 1;
    // Clone and stamp explicit px dimensions so the SVG has a concrete
    // intrinsic size when loaded as a standalone <img> blob URL.
    // Without this, percentage-based width/height attributes are unresolvable
    // outside a DOM containing block, causing the browser to use a fallback
    // size (typically 300×150) and clip or scale strokes incorrectly.
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('width', String(w));
    clone.setAttribute('height', String(h));
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(clone);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
      }
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.src = url;
  });
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ClientSignatureCanvasProps {
  /** Called after every completed stroke with the current PNG data URL. */
  onCapture: (dataUrl: string) => void;
  /** Called when the canvas is cleared. */
  onClear: () => void;
  /**
   * Previously captured PNG data URL (e.g. when navigating away and back).
   * When provided the component shows the saved image instead of an empty canvas
   * so the user sees their signature was preserved.
   */
  existingDataUrl?: string | undefined;
  error?: boolean | undefined;
  errorMessage?: string | undefined;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Freehand client signature canvas using @uiw/react-signature (SVG) and
 * perfect-freehand stroke rendering.
 *
 * Structure mirrors Figma exactly:
 * - Outer box: 228px height, borderRadius 26px, padding 12px, flex-col
 * - Row 1 (when strokes exist): right-aligned outlined "Clear" button
 * - Row 2 (flex-1): Signature SVG, or captured image preview, or placeholder
 * - Below box: helper text
 *
 * When `existingDataUrl` is provided (navigation back to this step), the
 * previously drawn signature is shown as an image so the user knows it is
 * preserved. Clearing it returns to the empty live canvas.
 *
 * No S3 upload — exports PNG data URL on pointer up.
 */
const ClientSignatureCanvas: React.FC<ClientSignatureCanvasProps> = ({
  onCapture,
  onClear,
  existingDataUrl,
  error,
  errorMessage,
}) => {
  const sigRef = useRef<SignatureRef>(null);
  const hasStrokes = useRef(false);
  const [isEmpty, setIsEmpty] = useState(true);
  // True when showing the previously captured image (e.g. after navigating back).
  const [showPreview, setShowPreview] = useState(!!existingDataUrl);

  // If the parent passes a new existingDataUrl after mount (e.g. step re-entry)
  // switch back to preview mode so the user sees their saved signature.
  // Guard: if the user has already drawn strokes this session the update is a
  // real-time data-URL refresh (not a navigation event) — don't switch to preview
  // mid-draw, that would unmount the live SVG and freeze the canvas.
  useEffect(() => {
    if (existingDataUrl && !hasStrokes.current) {
      setShowPreview(true);
      setIsEmpty(false);
    }
  }, [existingDataUrl]);

  // Keep latest onCapture in a ref so handlePointer stays a stable reference.
  // A changing onPointer prop causes @uiw/react-signature to reset mid-stroke.
  const onCaptureRef = useRef(onCapture);
  useEffect(() => {
    onCaptureRef.current = onCapture;
  }, [onCapture]);

  const handlePointer = useCallback(async (points: number[][]) => {
    if (points.length === 0) {
      return;
    }
    hasStrokes.current = true;
    setIsEmpty(false);
    const svg = sigRef.current?.svg;
    if (!svg) {
      return;
    }
    const dataUrl = await svgToDataUrl(svg);
    onCaptureRef.current(dataUrl);
  }, []);

  // @uiw/react-signature only invokes onPointer on pointer-up (stroke complete),
  // so isEmpty wouldn't flip until the stroke finishes — leaving the placeholder
  // overlapping the ink for the whole first stroke. Listen for pointerdown on the
  // SVG directly so the placeholder hides the instant drawing starts.
  useEffect(() => {
    const svg = sigRef.current?.svg;
    if (!svg) {
      return;
    }
    const handlePointerDown = () => setIsEmpty(false);
    svg.addEventListener('pointerdown', handlePointerDown);
    return () => svg.removeEventListener('pointerdown', handlePointerDown);
  }, [showPreview]);

  const handleClear = useCallback(() => {
    sigRef.current?.clear();
    hasStrokes.current = false;
    setIsEmpty(true);
    setShowPreview(false);
    onClear();
  }, [onClear]);

  const hasContent = !isEmpty || showPreview;

  return (
    <div className="flex flex-col gap-2">
      {/* Canvas */}
      <div
        style={{
          height: 228,
          borderRadius: 26,
          padding: 12,
          background: '#ffffff',
          touchAction: 'none',
          border: error ? '2px dashed #e11b1b' : '2px dashed #D1D5DB',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          userSelect: 'none',
        }}
      >
        <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
          {/* Clear button — shown whenever there is content */}
          {hasContent && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 2,
                border: '1px solid #DCDDE0',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 14,
                fontWeight: 500,
                color: '#474A4C',
                lineHeight: '20px',
                letterSpacing: '0.1px',
                background: '#ffffff',
                cursor: 'pointer',
              }}
            >
              {L.CLIENT_SIGNATURE_CLEAR}
            </button>
          )}

          {/* Previously captured signature image — shown when navigating back */}
          {showPreview && existingDataUrl ? (
            <img
              src={existingDataUrl}
              alt="Podpis klienta"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                display: 'block',
              }}
            />
          ) : (
            <>
              <Signature
                ref={sigRef}
                onPointer={handlePointer}
                options={{
                  size: 4,
                  thinning: 0.5,
                  smoothing: 0.5,
                  streamline: 0.5,
                  simulatePressure: false,
                }}
                style={{ width: '100%', height: '100%', display: 'block' }}
              />

              {/* Placeholder — centred, only when canvas is empty */}
              {isEmpty && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <p
                    style={{
                      fontSize: 14,
                      color: '#474A4C',
                      lineHeight: '20px',
                    }}
                  >
                    {L.CLIENT_SIGNATURE_PLACEHOLDER}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Helper text */}
      <div className="px-4">
        <p className="text-xs leading-relaxed" style={{ color: '#474A4C' }}>
          {L.CLIENT_SIGNATURE_HELPER}
        </p>
      </div>

      {errorMessage && (
        <p className="text-xs px-4" style={{ color: '#E74141' }}>
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default ClientSignatureCanvas;
